import { useCallback, useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { Zeichen, type ZeichenName } from '../ui/Zeichen.js'
import { notify, notifyError } from '../ui/Toasts.js'
import {
  api,
  type DashboardDaten,
  type DashboardAufgabe,
  type DashboardRechnung,
  type Sprung
} from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Das Dashboard – eine Arbeitsliste, kein Statistikbrett.
 *
 * Die Anordnung beantwortet die Fragen in der Reihenfolge, in der sie ein
 * Betrieb morgens stellt: was ist liegengeblieben, was ist heute dran, was
 * kommt diese Woche, wo fehlt Geld, was liegt auf den Baustellen herum.
 *
 * Zwei Blöcke sehen ähnlich aus und sind grundverschieden, deshalb heissen sie
 * hier auch verschieden:
 *
 *   AUFGABEN hat jemand selbst an einen Beleg geschrieben. Sie haben einen
 *   Knopf «Erledigt», und wer drückt, steht danach in der Datenbank.
 *
 *   HINWEISE rechnet der Server aus Belegen, Zahlungen und Rapporten. Sie
 *   haben keinen «Erledigt»-Knopf, sondern führen zu der Stelle, an der man
 *   die Ursache löst. Ein abgehakter Hinweis auf eine unbezahlte Rechnung
 *   wäre genau die Art Beruhigung, die einen Betrieb Geld kostet.
 */

/* ---------------------------------------------------------------------------
   Der Geldstrahl

   Vorher stand hier ein Balkendiagramm: offene Beträge nach Monat, aus dem
   BELEGDATUM. Das beantwortete die Frage «wann haben wir gestellt» – und die
   stellt im Büro niemand. Gefragt wird, wann Geld hereinkommt und was schon
   zu lange draussen ist.

   Also eine Achse über 56 Tage: 25 zurück, heute, 30 voraus. Links vom Faden
   liegt, was überfällig ist, rechts, was kommt. Die Höhe ist der offene
   Betrag, die Farbe der Zustand. Man rechnet nicht – man sieht eine Lage.

   Rechnungen, deren Fälligkeit ausserhalb des Fensters liegt, werden auf den
   Rand geschoben und NICHT weggelassen. Sonst stünde unter dem Strahl eine
   andere Summe als in der Kachel daneben, und zwei Wahrheiten über dasselbe
   Geld sind schlimmer als eine ungenaue Achse. Die Beschriftung sagt es an.
   --------------------------------------------------------------------------- */

const STRAHL_ZURUECK = 25
const STRAHL_VORAUS = 30
const STRAHL_TAGE = STRAHL_ZURUECK + 1 + STRAHL_VORAUS

interface StrahlSaeule {
  tag: number
  rp: number
  art: 'spaet' | 'faellig' | 'rein'
  hinweis: string
  /** Der Fälligkeitstag, auf dem diese Säule steht – für den Sprung in die Liste. */
  datum: string
}

interface Strahl {
  saeulen: StrahlSaeule[]
  groesster: number
  offenRp: number
  spaetRp: number
  heuteProzent: number
  linksText: string
  rechtsText: string
}

/** Ganze Tage zwischen zwei ISO-Daten – ohne Zeitzonenrutsch. */
function tageZwischen(vonIso: string, bisIso: string): number {
  const a = Date.parse(`${vonIso}T00:00:00Z`)
  const b = Date.parse(`${bisIso}T00:00:00Z`)
  return Math.round((b - a) / 86400000)
}

function geldstrahl(daten: DashboardDaten): Strahl {
  const saeulen: StrahlSaeule[] = Array.from({ length: STRAHL_TAGE }, (_, tag) => ({
    tag,
    rp: 0,
    art: 'faellig' as const,
    hinweis: '',
    datum: new Date(Date.parse(`${daten.heute}T00:00:00Z`) + (tag - STRAHL_ZURUECK) * 86400000)
      .toISOString()
      .slice(0, 10)
  }))
  let ausserhalb = 0

  for (const r of daten.rechnungen.items) {
    if (r.offenRp <= 0) continue
    /*
      Ohne Fälligkeit gibt es keinen Platz auf einer Zeitachse. Solche
      Rechnungen kommen ganz nach links: sie sind gestellt, das Geld fehlt,
      und niemand weiss bis wann – das ist eher zu spät als zu früh.
    */
    const versatz = r.dueDate ? STRAHL_ZURUECK + tageZwischen(daten.heute, r.dueDate) : 0
    const tag = Math.max(0, Math.min(STRAHL_TAGE - 1, versatz))
    if (versatz !== tag) ausserhalb++
    const s = saeulen[tag]
    s.rp += r.offenRp
    if (r.ueberfaellig) s.art = 'spaet'
  }

  const offenRp = daten.rechnungen.summe.offenRp
  const spaetRp = daten.rechnungen.summe.ueberfaelligRp
  const groesster = saeulen.reduce((m, s) => Math.max(m, s.rp), 0) || 1

  for (const s of saeulen) {
    if (s.rp === 0) continue
    const d = new Date(Date.parse(`${daten.heute}T00:00:00Z`) + (s.tag - STRAHL_ZURUECK) * 86400000)
    const wann = d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })
    const was = s.art === 'spaet' ? `überfällig seit ${wann}` : `fällig am ${wann}`
    /* Der Zusatz ist kein Schmuck: Eine Säule, die sich anklicken lässt, muss
       es sagen – im Titel steht sonst nur eine Zahl. */
    s.hinweis = `CHF ${rp(s.rp)} · ${was} · öffnet die Rechnungen`
  }

  return {
    saeulen,
    groesster,
    offenRp,
    spaetRp,
    heuteProzent: ((STRAHL_ZURUECK + 0.5) / STRAHL_TAGE) * 100,
    linksText:
      ausserhalb > 0 ? `vor ${STRAHL_ZURUECK} Tagen und älter` : `vor ${STRAHL_ZURUECK} Tagen`,
    rechtsText: `in ${STRAHL_VORAUS} Tagen`
  }
}

/* Die Balkenhöhe reist als eigene CSS-Eigenschaft ins Markup. */
type BalkenHoehe = React.CSSProperties & { '--h': string }

const rp = (n: number): string =>
  (n / 100).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const datum = (iso: string | null): string =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('de-CH') : '–'

/**
 * Der Satz unter einer gekürzten Liste.
 *
 * Eine Liste, die schweigend bei dreihundert aufhört, behauptet, dreihundert
 * seien alle. Das ist die billigste Art, Vertrauen zu verlieren: Die Zahl
 * darüber stimmt, die Liste darunter ist unvollständig, und nichts sagt es.
 *
 * Die SUMMEN auf dieser Seite sind davon nicht betroffen – sie kommen aus
 * eigenen Abfragen ohne Grenze. Gekürzt ist nur, was man sieht.
 */
function Kuerzung({
  gezeigt,
  gesamt,
  was
}: {
  gezeigt: number
  gesamt?: number
  /** Wonach sortiert wurde – sonst weiss niemand, WELCHE er sieht. */
  was: string
}): JSX.Element | null {
  if (gesamt === undefined || gesamt <= gezeigt) return null
  return (
    <span className="o-soft">
      Zeigt {gezeigt} {was} von {gesamt}. Die Beträge oben zählen alle.
    </span>
  )
}

const ZAHLSTATUS: Record<string, { text: string; klasse: string }> = {
  offen: { text: 'offen', klasse: 'o-badge-neutral' },
  teilbezahlt: { text: 'teilbezahlt', klasse: 'o-badge-warn' },
  bezahlt: { text: 'bezahlt', klasse: 'o-badge-ok' },
  ueberbezahlt: { text: 'überbezahlt', klasse: 'o-badge-alert' },
  storniert: { text: 'storniert', klasse: 'o-badge-neutral' }
}

export function Dashboard({
  session,
  aufBeleg,
  aufBereich,
  aufListe,
  aufHinweiszahl
}: {
  session: SessionUser
  /** Springt in die Rechnung. Die Liste ist ein Sprungbrett, kein Ziel. */
  aufBeleg?: (id: string) => void
  aufBereich?: (id: string) => void
  /**
   * Springt in eine Liste und stellt sie gleich richtig ein.
   *
   * Ohne das führte jede Kachel nur in den Bereich, und dort stand der
   * Normalfilter: Wer auf «8 überfällig» tippt, sah danach alle offenen
   * Rechnungen und durfte die acht selbst suchen.
   */
  aufListe?: (ziel: Sprung) => void
  /** Meldet der Kopfleiste, wie viele Hinweise offen sind – für die Glocke. */
  aufHinweiszahl?: (n: number) => void
}): JSX.Element {
  const [daten, setDaten] = useState<DashboardDaten | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  const laden = useCallback(async (): Promise<void> => {
    try {
      const frisch = await api.dashboard()
      setDaten(frisch)
      /* Die Glocke bekommt ihre Zahl von hier – ein zweiter Aufruf wäre Verkehr für eine Ziffer. */
      aufHinweiszahl?.(frisch.hinweise.length)
    } catch (err) {
      notifyError(err)
    } finally {
      setLaedt(false)
    }
  }, [aufHinweiszahl])

  useEffect(() => {
    void laden()
  }, [laden])

  /**
   * Aufgabe abhaken.
   *
   * Der Eintrag verschwindet aus der Liste; der Beleg bleibt, wie er war. Was
   * hier erledigt wird, ist die Notiz – nicht die Rechnung.
   */
  async function erledigen(a: DashboardAufgabe): Promise<void> {
    setBusy(a.id)
    try {
      await api.setNextAction(
        a.id,
        { label: a.label, date: a.date, note: a.note, done: true },
        uuidv7()
      )
      notify.ok('Erledigt.')
      await laden()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(null)
    }
  }

  if (laedt) {
    return (
      <>
        <OfficeHead titel="Übersicht" unter="Wird geladen …" />
        <div className="o-card">
          <div className="o-pad">
            <p className="o-muted" style={{ margin: 0 }}>
              Einen Moment.
            </p>
          </div>
        </div>
      </>
    )
  }

  if (!daten) {
    return (
      <>
        <OfficeHead titel="Übersicht" />
        <div className="o-card">
          <div className="o-pad">
            <p style={{ marginTop: 0 }}>Die Arbeitsliste liess sich nicht laden.</p>
            <button className="o-btn" onClick={() => void laden()}>
              Nochmal versuchen
            </button>
          </div>
        </div>
      </>
    )
  }

  const offenGesamt = daten.rechnungen.summe.offenRp
  /*
    Zwei getrennte Zahlen, weil es zwei getrennte Dinge sind.

    `faelligHeute` sind Aufgaben mit einem Datum, das heute oder vorbei ist –
    nur die dürfen «fällig» heissen. `offeneEnden` sind die abgeleiteten
    Hinweise: ein Entwurf, eine nicht versendete Rechnung, nicht verrechnete
    Rapporte. Die haben kein Datum und sind deshalb nie «heute fällig».

    Vorher wurden beide zu `zuTun` addiert und das Ergebnis mit «brauchen heute
    eine Hand» beschriftet.
  */
  const faelligHeute = daten.aufgaben.ueberfaellig.length + daten.aufgaben.heute.length
  const offeneEnden = daten.hinweise.length
  const zuTun = faelligHeute + offeneEnden

  /*
    Die offenen Beträge nach Monat.

    Gerechnet aus den offenen Rechnungen, die ohnehin schon geladen sind – kein
    zweiter Aufruf. Bewusst NICHT «Umsatzentwicklung» genannt: Umsatz wären
    alle abgeschlossenen Belege, und die liegen hier nicht. Ein Diagramm, das
    «Umsatz» heisst und offene Posten zeigt, ist die Sorte Zahl, nach der
    jemand eine Entscheidung trifft, die auf nichts steht.

    Die letzten zwölf Monate, auch die leeren – eine Lücke ist eine Aussage.
  */
  const strahl = geldstrahl(daten)

  /*
    Was heute ansteht: die gesetzten Aufgaben zuerst, danach die abgeleiteten
    Hinweise. Beides echt, beides aus derselben Antwort – nur zusammengelegt,
    weil auf diesem Platz die Frage «was muss ich anfassen» lautet und nicht
    «woher kommt der Eintrag».
  */
  const aktivitaeten: Array<{
    schluessel: string
    zeichen: ZeichenName
    ton: 'alarm' | 'warn' | 'ok'
    titel: string
    unter: string
  }> = [
    ...daten.aufgaben.ueberfaellig.map((a) => ({
      schluessel: `a-${a.id}`,
      zeichen: 'kalender' as ZeichenName,
      ton: 'alarm' as const,
      titel: a.label ?? 'Aufgabe',
      unter: `${a.customerName} · überfällig`
    })),
    ...daten.aufgaben.heute.map((a) => ({
      schluessel: `h-${a.id}`,
      zeichen: 'haken' as ZeichenName,
      ton: 'ok' as const,
      titel: a.label ?? 'Aufgabe',
      unter: `${a.customerName} · heute`
    })),
    ...daten.hinweise.map((h, i) => ({
      schluessel: `w-${i}`,
      zeichen: 'rechnung' as ZeichenName,
      ton: h.dringlichkeit >= 3 ? ('alarm' as const) : ('warn' as const),
      titel: h.titel,
      unter: h.text
    }))
    /*
    Vier Zeilen, wie in der Vorlage. Mehr macht den Kasten höher als den
    daneben, und zwei ungleich hohe Kästen nebeneinander sehen aus, als wäre
    einer abgeschnitten. Der Rest steht weiter unten in voller Länge.
  */
  ].slice(0, 4)

  function Aufgabenblock({
    titel,
    unter,
    eintraege,
    ton,
    id
  }: {
    titel: string
    unter?: string
    eintraege: DashboardAufgabe[]
    ton?: 'alarm'
    id: string
  }): JSX.Element | null {
    if (eintraege.length === 0) return null
    return (
      <div className="o-card" id={id}>
        <div className="o-card-head">
          <h3>
            {titel}{' '}
            <span className={`o-badge ${ton === 'alarm' ? 'o-badge-alert' : 'o-badge-accent'}`}>
              {eintraege.length}
            </span>
          </h3>
          {unter && <span className="o-soft">{unter}</span>}
        </div>
        <div className="o-tabelle">
          <table className="o-data">
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Aufgabe</th>
                <th style={{ width: 110 }}>Fällig</th>
                <th>Kunde</th>
                <th style={{ width: 140 }}>Beleg</th>
                <th className="o-num" style={{ width: 120 }}>
                  Offen
                </th>
                <th style={{ width: 220 }} />
              </tr>
            </thead>
            <tbody>
              {eintraege.map((a) => (
                <tr key={a.id} id={`aufgabe-${a.id}`}>
                  <td>
                    <strong>{a.label}</strong>
                    {a.note && <div className="o-soft">{a.note}</div>}
                  </td>
                  <td>{datum(a.date)}</td>
                  <td>
                    {a.customerName}
                    {a.projectName && <div className="o-soft">{a.projectName}</div>}
                  </td>
                  <td>
                    {a.number ? (
                      <span className="o-mono">{a.number}</span>
                    ) : (
                      <span className="o-badge o-badge-neutral">Entwurf</span>
                    )}
                  </td>
                  <td className="o-num">{a.offenRp === null ? '–' : rp(a.offenRp)}</td>
                  <td>
                    <div className="office-actions">
                      <button
                        className="o-btn o-btn-ghost"
                        id={`oeffnen-${a.id}`}
                        onClick={() => aufBeleg?.(a.id)}
                      >
                        Beleg öffnen
                      </button>
                      <button
                        className="o-btn"
                        id={`erledigt-${a.id}`}
                        disabled={busy !== null}
                        onClick={() => void erledigen(a)}
                      >
                        {busy === a.id ? 'Speichert …' : 'Erledigt'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <>
      <OfficeHead
        titel="Übersicht"
        /*
          Die Unterzeile sagt jetzt dasselbe wie der Kasten daneben.

          Sie zählte `zuTun` – Aufgaben mit Datum plus Hinweise der höchsten
          Dringlichkeit. Zwei Hinweise ohne Datum ergaben dort null, und die
          Seite behauptete «Nichts liegt an», während rechts daneben zwei
          Zeilen standen. Widersprüche auf derselben Seite kosten mehr
          Vertrauen, als eine zu volle Liste je kosten könnte.

          Zwei Zahlen, zwei Aussagen: was heute oder früher fällig ist, und was
          sonst noch offen herumliegt.
        */
        unter={
          faelligHeute > 0
            ? faelligHeute === 1
              ? `Ein Punkt ist heute fällig${offeneEnden > 0 ? `, ${offeneEnden} weitere sind offen` : ''}.`
              : `${faelligHeute} Punkte sind heute fällig${offeneEnden > 0 ? `, ${offeneEnden} weitere sind offen` : ''}.`
            : offeneEnden > 0
              ? offeneEnden === 1
                ? 'Heute ist nichts fällig. Ein Punkt liegt offen.'
                : `Heute ist nichts fällig. ${offeneEnden} Punkte liegen offen.`
              : 'Nichts offen. Offene Rechnungen stehen weiter unten.'
        }
      >
        {/*
          Express-Rechnung zuerst und in der auffälligen Farbe. Das Dashboard
          ist die Startseite, aber Rechnungschreiben bleibt der häufigste
          Handgriff des Tages – er darf keinen Umweg kosten.
        */}
        <button
          className="o-btn o-btn-primary"
          id="btn-schnell-express"
          onClick={() => aufBereich?.('express')}
        >
          Express-Rechnung
        </button>
        <button
          className="o-btn"
          id="btn-schnell-offerte"
          onClick={() => aufBereich?.('expressofferte')}
        >
          Express-Offerte
        </button>
        {/*
          Kunden, Zahlungen und Rapporte stehen in der Seitenleiste – ein
          zweiter Weg dorthin macht die Titelzeile voll und spart keinen Klick.
          Express-Rechnung und Express-Offerte stehen dort nicht, deshalb
          bleiben genau diese zwei hier.
        */}
      </OfficeHead>

      {/* ---------- Vier Zahlen, nach denen wirklich gefragt wird ---------- */}

      {/*
        Jede Karte trägt eine Anzahl und darunter den Betrag, um den es geht.
        Die Anzahl beantwortet «wie viel Arbeit», der Betrag «wie viel Geld» –
        zusammen die zwei Fragen, mit denen jemand morgens hereinkommt.

        Alle vier Zahlen sind gerechnet, keine davon steht in einer Spalte.

        Oben rechts steht das Zeichen des Bereichs, zu dem die Zahl gehört.
        Es ist derselbe Griff wie in der Seitenleiste – wer «Rechnungen»
        sucht, sucht dasselbe Bild. Was hier bewusst NICHT steht, ist ein
        Pfeil mit «+12 % gegenüber Vormonat»: dafür müsste der Vormonat
        gerechnet werden, und er wird es nicht. Eine erfundene Veränderung
        wäre schlimmer als keine.
      */}
      {/*
        Die Kacheln sind Knöpfe.

        Sie sahen schon immer anklickbar aus – eigene Fläche, Schatten, ein
        Anheben beim Hinfahren – und waren es nicht. Wer «8 überfällig» liest,
        will die acht sehen und nicht den Bereich «Rechnungen» mit dem
        Normalfilter. Jede Kachel führt deshalb in die Liste, die ihre eigene
        Zahl ergibt: derselbe Filter, dieselbe Bedingung, dieselbe Zahl.
      */}
      <div className="o-kennzahlen" id="kennzahlen">
        {/*
          Eine Kachel, die null zeigt, führt nirgendwo hin.

          Sie bleibt stehen – «null überfällig» ist die Nachricht, auf die der
          Betrieb hinarbeitet, und eine verschwundene Kachel wäre keine
          Antwort. Anklickbar ist sie nicht: Ein Klick, der eine leere Liste
          öffnet, ist ein Weg mit Rückweg und ohne Zweck. Dieselbe Regel gilt
          für die leeren Säulen im Geldstrahl.
        */}
        <button
          type="button"
          className="o-kennzahl"
          id="kz-karte-offen"
          disabled={daten.rechnungen.summe.anzahl === 0}
          onClick={() => aufListe?.({ bereich: 'rechnungen', filter: 'offen' })}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="rechnung" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Offene Rechnungen</span>
          <span className="o-kennzahl-wert" id="kz-offene-rechnungen">
            {daten.rechnungen.summe.anzahl}
          </span>
          <span className="o-kennzahl-unter ok" id="kz-offen">
            CHF {rp(offenGesamt)}
          </span>
        </button>

        <button
          type="button"
          className="o-kennzahl"
          id="kz-karte-offerten"
          disabled={(daten.offerten.gesamt?.offen ?? daten.offerten.offen?.length ?? 0) === 0}
          onClick={() => aufListe?.({ bereich: 'offerten', filter: 'offen' })}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="offerte" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Offene Offerten</span>
          <span className="o-kennzahl-wert" id="kz-offene-offerten">
            {/*
              Die echte Zahl aus dem Aggregat, nicht die Länge der Liste.
              Die Liste ist begrenzt; die Kachel darf es nicht sein.
            */}
            {daten.offerten.gesamt?.offen ?? daten.offerten.offen?.length ?? 0}
          </span>
          <span className="o-kennzahl-unter ok">CHF {rp(daten.offerten.summe?.offenRp ?? 0)}</span>
        </button>

        <button
          type="button"
          className="o-kennzahl"
          id="kz-karte-rapporte"
          disabled={daten.rapporte.summe.projekte === 0}
          onClick={() => aufBereich?.('rapporte')}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="projekt" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Projekte mit Rapporten</span>
          <span className="o-kennzahl-wert" id="kz-baustellen">
            {daten.rapporte.summe.projekte}
          </span>
          <span className="o-kennzahl-unter ok">
            {daten.rapporte.summe.rapporte} Rapporte offen
          </span>
        </button>

        <button
          type="button"
          id="kz-karte-ueberfaellig"
          className={
            daten.rechnungen.summe.ueberfaelligAnzahl > 0 ? 'o-kennzahl alarm' : 'o-kennzahl'
          }
          disabled={daten.rechnungen.summe.ueberfaelligAnzahl === 0}
          onClick={() => aufListe?.({ bereich: 'rechnungen', filter: 'ueberfaellig' })}
        >
          <span className="o-kennzahl-zeichen" aria-hidden="true">
            <Zeichen name="zahlung" groesse={17} />
          </span>
          <span className="o-kennzahl-label">Überfällige Zahlungen</span>
          <span className="o-kennzahl-wert" id="kz-ueberfaellig-anzahl">
            {daten.rechnungen.summe.ueberfaelligAnzahl}
          </span>
          <span
            className={
              daten.rechnungen.summe.ueberfaelligAnzahl > 0
                ? 'o-kennzahl-unter alarm'
                : 'o-kennzahl-unter ok'
            }
            id="kz-ueberfaellig"
          >
            CHF {rp(daten.rechnungen.summe.ueberfaelligRp)}
          </span>
        </button>
      </div>

      {/* ---------- Verteilung und Aktivitäten nebeneinander ---------- */}

      <div className="o-zwei">
        <div className="o-kasten">
          <div className="o-kasten-kopf">
            <b>Geld in den nächsten Wochen</b>
            <i>nach Fälligkeit</i>
          </div>
          {strahl.saeulen.every((s) => s.rp === 0) ? (
            <p className="o-leer">Nichts fällig und nichts überfällig.</p>
          ) : (
            <>
              <div className="o-strahl-kopf">
                <b id="strahl-offen">CHF {rp(strahl.offenRp)}</b>
                <span>
                  offen
                  {strahl.spaetRp > 0 && (
                    <>
                      {' · davon '}
                      <em>CHF {rp(strahl.spaetRp)}</em>
                      {' zu lange'}
                    </>
                  )}
                </span>
              </div>

              {/*
                Jede Säule führt zu ihren Rechnungen.

                Eine Säule steht auf einem Fälligkeitstag, und genau danach
                kann die Rechnungsliste filtern – seit es dort den Zeitraum
                mit wählbarer Datumsart gibt. Also: Filter «offen», Datumsart
                «Fälligkeit», von und bis auf diesen Tag. Kein zweiter Begriff
                von Zeitraum, keine neue Abfrage.

                Die erste Säule ist der Sammelplatz: Dort liegt alles, was
                älter ist als der linke Rand. Sie öffnet deshalb «bis zu
                diesem Tag» statt «an diesem Tag».

                Leere Säulen sind keine Knöpfe. Ein Klick, der eine leere
                Liste öffnet, ist eine Enttäuschung mit Umweg.
              */}
              <div className="o-strahl" id="geldstrahl">
                {strahl.saeulen.map((s) =>
                  s.rp === 0 ? (
                    <span key={s.tag} className="o-saeule leer" />
                  ) : (
                    <button
                      type="button"
                      key={s.tag}
                      className={`o-saeule ${s.art}`}
                      title={s.hinweis}
                      aria-label={s.hinweis}
                      onClick={() =>
                        aufListe?.({
                          bereich: 'rechnungen',
                          filter: 'offen',
                          datum: { art: 'faellig', von: s.tag === 0 ? '' : s.datum, bis: s.datum }
                        })
                      }
                      style={
                        { '--h': `${Math.round((s.rp / strahl.groesster) * 100)}%` } as BalkenHoehe
                      }
                    />
                  )
                )}
                <span className="o-heute" style={{ left: `${strahl.heuteProzent}%` }}>
                  heute
                </span>
              </div>

              <div className="o-strahl-fuss">
                <span>{strahl.linksText}</span>
                <span>{strahl.rechtsText}</span>
              </div>
            </>
          )}
        </div>

        {/*
          «Offen» statt «heute».

          Über diesem Kasten stand «Was heute ansteht», und darunter standen
          drei verschiedene Dinge: Aufgaben mit überschrittenem Datum, Aufgaben
          mit dem heutigen Datum – und abgeleitete Hinweise, die gar kein Datum
          haben. Ein Entwurf, der vor zehn Minuten entstanden ist, und eine
          Offerte, deren nächster Schritt in einem Monat liegt, standen damit
          unter einer Überschrift, die «heute fällig» behauptet. Das ist
          schlicht falsch, und es macht die Überschrift wertlos: wer zweimal
          liest, dass etwas heute ansteht, und zweimal feststellt, dass es
          nicht stimmt, liest beim dritten Mal nicht mehr hin.

          Der Kasten heisst deshalb, was er zeigt: was offen ist. Was
          tatsächlich ein Datum hat, sagt es in seiner eigenen Zeile –
          «überfällig» oder «heute»; der Rest sagt nichts über ein Datum und
          behauptet jetzt auch nichts mehr.
        */}
        <div className="o-kasten">
          <div className="o-kasten-kopf">
            <b>Was offen ist</b>
            {zuTun > 0 && <i>{zuTun} Punkte</i>}
          </div>
          {aktivitaeten.length === 0 ? (
            <p className="o-leer">Nichts offen. Die Liste füllt sich von selbst.</p>
          ) : (
            <div className="o-aktiv">
              {aktivitaeten.map((a) => (
                <div className="o-aktiv-zeile" key={a.schluessel}>
                  <span className={`o-aktiv-zeichen ${a.ton}`}>
                    <Zeichen name={a.zeichen} groesse={17} />
                  </span>
                  <span className="o-aktiv-wort">
                    <b>{a.titel}</b>
                    <i>{a.unter}</i>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---------- Gespeicherte Aufgaben ---------- */}

      <div className="office-section">Nächste Schritte</div>

      <Aufgabenblock
        id="block-ueberfaellig"
        titel="Überfällig"
        unter="Das Datum ist vorbei."
        ton="alarm"
        eintraege={daten.aufgaben.ueberfaellig}
      />
      <Aufgabenblock id="block-heute" titel="Heute zu erledigen" eintraege={daten.aufgaben.heute} />
      <Aufgabenblock id="block-7tage" titel="Nächste 7 Tage" eintraege={daten.aufgaben.naechste7} />
      <Aufgabenblock
        id="block-ohnedatum"
        titel="Ohne Datum"
        unter="Notiert, aber auf keinen Tag festgelegt."
        eintraege={daten.aufgaben.ohneDatum}
      />

      {daten.aufgaben.ueberfaellig.length +
        daten.aufgaben.heute.length +
        daten.aufgaben.naechste7.length +
        daten.aufgaben.ohneDatum.length ===
        0 && (
        <div className="o-card" id="block-keine-aufgaben">
          <div className="o-pad">
            <p style={{ margin: 0 }} className="o-muted">
              Kein nächster Schritt notiert. Beim Abschliessen einer Rechnung lässt sich unter
              „Optionen" eine Aufgabe mitgeben – etwa „Zahlungseingang prüfen".
            </p>
          </div>
        </div>
      )}

      {/* ---------- Abgeleitete Hinweise ---------- */}

      {daten.hinweise.length > 0 && (
        <>
          <div className="office-section">Hinweise aus den Daten</div>
          <div className="o-card" id="block-hinweise">
            <div className="o-card-head">
              <h3>
                Was das System sieht{' '}
                <span className="o-badge o-badge-neutral">{daten.hinweise.length}</span>
              </h3>
              <span className="o-soft">
                Nicht abhakbar – sie verschwinden, wenn die Ursache weg ist.
              </span>
            </div>
            <div className="o-tabelle">
              <table className="o-data">
                <tbody>
                  {daten.hinweise.slice(0, 12).map((h) => (
                    <tr key={`${h.art}-${h.id}`} id={`hinweis-${h.art}-${h.id}`}>
                      <td style={{ width: 130 }}>
                        <span
                          className={
                            h.dringlichkeit === 1
                              ? 'o-badge o-badge-alert'
                              : h.dringlichkeit === 2
                                ? 'o-badge o-badge-warn'
                                : 'o-badge o-badge-neutral'
                          }
                        >
                          {h.dringlichkeit === 1
                            ? 'kaputt'
                            : h.dringlichkeit === 2
                              ? 'dringend'
                              : 'offen'}
                        </span>
                      </td>
                      <td>
                        <strong>{h.titel}</strong>
                        <div className="o-soft">{h.text}</div>
                      </td>
                      <td className="o-num" style={{ width: 120 }}>
                        {h.offenRp ? rp(h.offenRp) : ''}
                      </td>
                      <td style={{ width: 200 }}>
                        <button
                          className="o-btn o-btn-ghost"
                          onClick={() =>
                            h.ziel.typ === 'beleg'
                              ? aufBeleg?.(h.ziel.id)
                              : aufBereich?.('rapporte')
                          }
                        >
                          {h.aktion}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {daten.hinweise.length > 12 && (
              <div className="o-pad">
                <button className="o-btn o-btn-ghost" onClick={() => aufBereich?.('offen')}>
                  Alle {daten.hinweise.length} in „Was ist offen"
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ---------- Offene Rechnungen ---------- */}

      <div className="office-section">Offene Rechnungen</div>

      <div className="o-card" id="block-rechnungen">
        <div className="o-card-head">
          <h3>
            Nicht bezahlt{' '}
            <span className="o-badge o-badge-accent">{daten.rechnungen.summe.anzahl}</span>
          </h3>
          <Kuerzung
            gezeigt={daten.rechnungen.items.length}
            gesamt={daten.rechnungen.gesamt}
            was="mit der frühesten Fälligkeit"
          />
          {!daten.rechnungen.gekuerzt && (
            <span className="o-soft">Zahlungsstatus aus den erfassten Zahlungen gerechnet.</span>
          )}
        </div>
        {daten.rechnungen.items.length === 0 ? (
          <div className="o-pad">
            <p className="o-muted" style={{ margin: 0 }}>
              Alles bezahlt.
            </p>
          </div>
        ) : (
          <div className="o-tabelle">
            <table className="o-data">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Nummer</th>
                  <th>Kunde</th>
                  <th style={{ width: 110 }}>Fällig</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th className="o-num" style={{ width: 120 }}>
                    Total
                  </th>
                  <th className="o-num" style={{ width: 120 }}>
                    Offen
                  </th>
                  <th style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {daten.rechnungen.items.map((r: DashboardRechnung) => (
                  <tr key={r.id} id={`rechnung-${r.id}`}>
                    <td className="o-mono">{r.number}</td>
                    <td>{r.customerName}</td>
                    <td>
                      {datum(r.dueDate)}
                      {r.ueberfaellig && (
                        <div>
                          <span className="o-badge o-badge-alert">überfällig</span>
                        </div>
                      )}
                      {/*
                        Steht nur da, wo es sonst eine Lücke gäbe: eine Rechnung,
                        deren Frist vorbei ist und die trotzdem nicht «überfällig»
                        heisst. Ohne diesen Hinweis sähe das nach einem Fehler aus
                        – dabei ist es der Grund. Der Kunde hat nichts bekommen.
                      */}
                      {!r.versendet && (
                        <div>
                          <span className="o-badge o-badge-warn">nicht versendet</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`o-badge ${ZAHLSTATUS[r.zahlstatus]?.klasse ?? ''}`}>
                        {ZAHLSTATUS[r.zahlstatus]?.text ?? r.zahlstatus}
                      </span>
                    </td>
                    <td className="o-num">{rp(r.totalRp)}</td>
                    <td className="o-num">
                      <strong>{rp(r.offenRp)}</strong>
                    </td>
                    <td>
                      <button
                        className="o-btn o-btn-ghost"
                        id={`zahlung-${r.id}`}
                        onClick={() => aufBeleg?.(r.id)}
                      >
                        Zahlung erfassen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- Rapporte ---------- */}

      <div className="office-section">Von den Projekten</div>

      <div className="o-card" id="block-rapporte">
        <div className="o-card-head">
          <h3>
            Rapporte prüfen{' '}
            <span className="o-badge o-badge-accent">{daten.rapporte.summe.rapporte}</span>
          </h3>
          <Kuerzung
            gezeigt={daten.rapporte.projekte.length}
            gesamt={daten.rapporte.gesamt}
            was="mit dem ältesten Rapport"
          />
          {!daten.rapporte.gekuerzt && (
            <span className="o-soft">Nach Projekt zusammengefasst.</span>
          )}
        </div>
        {daten.rapporte.projekte.length === 0 ? (
          <div className="o-pad">
            <p className="o-muted" style={{ margin: 0 }}>
              Nichts offen aus den Projekten.
            </p>
          </div>
        ) : (
          <div className="o-tabelle">
            <table className="o-data">
              <thead>
                <tr>
                  <th>Projekt</th>
                  <th>Kunde</th>
                  <th className="o-num" style={{ width: 100 }}>
                    Rapporte
                  </th>
                  <th style={{ width: 120 }}>Ältester</th>
                  <th style={{ width: 200 }}>Zu klären</th>
                  <th style={{ width: 180 }} />
                </tr>
              </thead>
              <tbody>
                {daten.rapporte.projekte.map((p) => (
                  <tr key={p.projectId} id={`rapport-${p.projectId}`}>
                    <td>
                      <strong>{p.projectName}</strong>
                    </td>
                    <td>{p.customerName}</td>
                    <td className="o-num">{p.anzahl}</td>
                    <td>{datum(p.aeltester)}</td>
                    <td>
                      {p.preisOffen > 0 && (
                        <span className="o-badge o-badge-warn">{p.preisOffen} ohne Preis</span>
                      )}{' '}
                      {p.ohneFoto > 0 && (
                        <span className="o-badge o-badge-neutral">{p.ohneFoto} ohne Foto</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="o-btn o-btn-ghost"
                        onClick={() => aufBereich?.('rapporte')}
                      >
                        Rechnung daraus
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- Offerten ---------- */}

      {daten.offerten.gebaut && (
        <>
          <div className="office-section">Offerten</div>

          <div className="o-card" id="block-offerten">
            <div className="o-card-head">
              <h3>
                Offen{' '}
                <span className="o-badge o-badge-accent">
                  {daten.offerten.gesamt?.offen ?? daten.offerten.offen?.length ?? 0}
                </span>
              </h3>
              <span className="o-soft">
                {rp(daten.offerten.summe?.offenRp ?? 0)} CHF in Angeboten, die noch keine Antwort
                haben.
              </span>
              <Kuerzung
                /* Die Tabelle schneidet zusätzlich bei zwölf ab – gezählt wird, was wirklich dasteht. */
                gezeigt={Math.min(
                  12,
                  (daten.offerten.offen?.length ?? 0) +
                    (daten.offerten.abgelaufen?.length ?? 0) +
                    (daten.offerten.angenommenOhneRechnung?.length ?? 0)
                )}
                gesamt={
                  daten.offerten.gesamt &&
                  daten.offerten.gesamt.offen +
                    daten.offerten.gesamt.abgelaufen +
                    daten.offerten.gesamt.angenommenOhneRechnung
                }
                was="mit der nächsten Gültigkeit"
              />
            </div>
            {(daten.offerten.offen?.length ?? 0) === 0 &&
            (daten.offerten.abgelaufen?.length ?? 0) === 0 &&
            (daten.offerten.angenommenOhneRechnung?.length ?? 0) === 0 ? (
              <div className="o-pad">
                <p className="o-muted" style={{ margin: 0 }}>
                  Nichts offen. Neue Offerte über „Express-Offerte".
                </p>
              </div>
            ) : (
              <div className="o-tabelle">
                <table className="o-data">
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Nummer</th>
                      <th>Kunde</th>
                      <th style={{ width: 150 }}>Gültig bis</th>
                      <th className="o-num" style={{ width: 115 }}>
                        Total
                      </th>
                      <th style={{ width: 150 }}>Zustand</th>
                      <th style={{ width: 200 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      /*
                        Drei Töpfe in einer Tabelle, in der Reihenfolge, in der
                        sie Geld kosten: zugesagt und nicht verrechnet ist bares
                        Geld, das liegenbleibt; abgelaufen ist verlorene Arbeit;
                        "läuft bald ab" ist die letzte Gelegenheit nachzufassen.
                      */
                      ...(daten.offerten.angenommenOhneRechnung ?? []).map((q) => ({
                        q,
                        hinweis: 'zugesagt, noch nicht verrechnet',
                        klasse: 'o-badge-ok'
                      })),
                      ...(daten.offerten.abgelaufen ?? []).map((q) => ({
                        q,
                        hinweis: 'abgelaufen',
                        klasse: 'o-badge-alert'
                      })),
                      ...(daten.offerten.laufenAb ?? []).map((q) => ({
                        q,
                        hinweis: 'läuft bald ab',
                        klasse: 'o-badge-warn'
                      })),
                      ...(daten.offerten.offen ?? [])
                        .filter((q) => !(daten.offerten.laufenAb ?? []).some((x) => x.id === q.id))
                        .map((q) => ({ q, hinweis: 'offen', klasse: 'o-badge-accent' }))
                    ]
                      .slice(0, 12)
                      .map(({ q, hinweis, klasse }) => (
                        <tr key={q.id} id={`doff-${q.id}`}>
                          <td className="o-mono">{q.number ?? 'Entwurf'}</td>
                          <td>
                            {q.customerName}
                            {q.projectName && <div className="o-soft">{q.projectName}</div>}
                          </td>
                          <td>{datum(q.validUntil)}</td>
                          <td className="o-num">{rp(q.totalRp)}</td>
                          <td>
                            <span className={`o-badge ${klasse}`}>{hinweis}</span>
                          </td>
                          <td>
                            <button
                              className="o-btn o-btn-ghost"
                              id={`doff-oeffnen-${q.id}`}
                              onClick={() => aufBereich?.('offerten')}
                            >
                              In den Offerten
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
